import { useMemo, useState } from 'react';
import { parseCidr, validateBlockPlacement } from '../../apps/cidr-planner/cidrUtils';
import { usableIpCount } from '../../apps/cidr-planner/usableIpUtils';
import type { CidrBlock, IpCountMode } from '../../apps/cidr-planner/types';
import type { DisplayLang } from '../../../shared/utils/pageDisplay';
import { CidrSheet } from './CidrSheet';

type BlockDetailSheetProps = {
  block: CidrBlock;
  parentCidr: string;
  existingCidrs: string[];
  ipCountMode: IpCountMode;
  lang: DisplayLang;
  onClose: () => void;
  onSave: (payload: { cidr: string; name: string }) => void;
  onDelete: () => void;
};

const TEXT = {
  ja: {
    title: 'ブロック詳細',
    cidr: 'CIDR',
    name: '名前',
    usable: '使用可能 IP',
    normalized: '正規化',
    outOfParent: '親 CIDR の範囲外です',
    overlap: '既存ブロックと重複しています',
    invalid: 'CIDR 形式が正しくありません',
    awsTooSmall: 'AWS VPC では /28 より細かいサブネットは作成できません',
    cancel: 'キャンセル',
    save: '保存',
    delete: '削除',
    close: '閉じる',
  },
  en: {
    title: 'Block details',
    cidr: 'CIDR',
    name: 'Name',
    usable: 'Usable IPs',
    normalized: 'Normalized to',
    outOfParent: 'Outside the parent CIDR range',
    overlap: 'Overlaps an existing block',
    invalid: 'Invalid CIDR format',
    awsTooSmall: 'AWS VPC subnets cannot be smaller than /28',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    close: 'Close',
  },
} as const;

function mapError(
  error: string,
  t: { outOfParent: string; overlap: string; invalid: string; awsTooSmall: string },
): string {
  if (error === 'OUT_OF_PARENT') return t.outOfParent;
  if (error === 'OVERLAP') return t.overlap;
  if (error === 'AWS_TOO_SMALL') return t.awsTooSmall;
  return t.invalid;
}

export function BlockDetailSheet({
  block,
  parentCidr,
  existingCidrs,
  ipCountMode,
  lang,
  onClose,
  onSave,
  onDelete,
}: BlockDetailSheetProps) {
  const t = TEXT[lang];
  const [cidrInput, setCidrInput] = useState(block.cidr);
  const [name, setName] = useState(block.name);

  const others = useMemo(
    () => existingCidrs.filter((cidr) => cidr !== block.cidr),
    [existingCidrs, block.cidr],
  );

  const validation = useMemo(() => {
    if (!cidrInput.trim()) {
      return null;
    }
    return validateBlockPlacement(cidrInput, parentCidr, others, block.cidr, ipCountMode);
  }, [cidrInput, parentCidr, others, block.cidr, ipCountMode]);

  const prefix = validation?.ok ? parseCidr(validation.normalized).prefix : null;

  return (
    <CidrSheet title={t.title} closeLabel={t.close} onClose={onClose}>
      <label className="cidr-sheet__field">
        <span className="cidr-sheet__label">{t.cidr}</span>
        <input
          className="cidr-sheet__input"
          value={cidrInput}
          onChange={(event) => setCidrInput(event.target.value)}
          inputMode="decimal"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </label>
      {validation?.ok && validation.wasNormalized && (
        <p className="cidr-sheet__hint">
          {t.normalized}: {validation.normalized}
        </p>
      )}
      {validation?.ok && prefix !== null && (
        <p className="cidr-sheet__hint">
          {t.usable}: {usableIpCount(prefix, ipCountMode)}
        </p>
      )}
      {validation && !validation.ok && (
        <p className="cidr-sheet__error">{mapError(validation.error, t)}</p>
      )}
      <label className="cidr-sheet__field">
        <span className="cidr-sheet__label">{t.name}</span>
        <input
          className="cidr-sheet__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>

      <div className="cidr-sheet__actions">
        <button type="button" className="cidr-sheet__btn cidr-sheet__btn--danger" onClick={onDelete}>
          {t.delete}
        </button>
        <button type="button" className="cidr-sheet__btn" onClick={onClose}>
          {t.cancel}
        </button>
        <button
          type="button"
          className="cidr-sheet__btn cidr-sheet__btn--primary"
          disabled={!validation?.ok}
          onClick={() => {
            if (validation?.ok) {
              onSave({ cidr: validation.normalized, name: name.trim() });
            }
          }}
        >
          {t.save}
        </button>
      </div>
    </CidrSheet>
  );
}
